import { useEffect, useRef, useState, useMemo } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useLocation, useNavigate } from "react-router-dom";
import SessionWarningModal from "./SessionWarningModal";

// const IDLE_TIME = 10 * 1000;
// const IDLE_TIME = 50 * 60 * 1000;
const IDLE_TIME = 5 * 60 * 1000;
const WARNING_TIME = 10;

const ENABLE_SESSION_TIMEOUT = false;
// const ENABLE_SESSION_TIMEOUT = true;

function SessionIdleProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_TIME);
  const countdownRef = useRef(null);
  const hasLoggedOutRef = useRef(false);
  const publicPaths = useMemo(
    () => [
      "/login-area",
      "/Signup",
      "/privacy-policy",
      "/refund-policy",
      "/terms-condition",
      "/session-expired",
    ],
    [],
  );
  const isPublicRoute = useMemo(() => {
    return publicPaths.some((path) => location.pathname.startsWith(path));
  }, [location.pathname, publicPaths]);
  const isLoggedIn = !!localStorage.getItem("userIdToken");
  const shouldEnableIdle =
  ENABLE_SESSION_TIMEOUT &&
  isLoggedIn &&
  !isPublicRoute &&
  !hasLoggedOutRef.current;

  const clearSessionOnce = () => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;
    clearInterval(countdownRef.current);
    setShowWarning(false);
    localStorage.clear();
  };

  const logoutDueToIdle = () => {
    clearSessionOnce();
    navigate("/session-expired", { replace: true });
  };

  const logoutManually = () => {
    clearSessionOnce();
    navigate("/login-area", { replace: true });
  };

  const startCountdown = () => {
    clearInterval(countdownRef.current);
    setRemainingSeconds(WARNING_TIME);

    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          logoutDueToIdle();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const { reset } = useIdleTimer({
    timeout: IDLE_TIME,
    debounce: 500,
    enabled: ENABLE_SESSION_TIMEOUT && shouldEnableIdle && !showWarning,

    onIdle: () => {
      if (!shouldEnableIdle) return;
      setShowWarning(true);
      startCountdown();
    },
  });

  const continueSession = () => {
    clearInterval(countdownRef.current);
    setShowWarning(false);
    setRemainingSeconds(WARNING_TIME);
    reset();
  };

  useEffect(() => {
    if (shouldEnableIdle) {
      reset();
    }
  }, [location.pathname, shouldEnableIdle, reset]);

  useEffect(() => {
    return () => clearInterval(countdownRef.current);
  }, []);

  return (
    <>
      {children}

      {shouldEnableIdle && (
        <SessionWarningModal
          open={showWarning}
          seconds={remainingSeconds}
          onContinue={continueSession}
          onLogout={logoutManually}
        />
      )}
    </>
  );
}

export default SessionIdleProvider;